/**
 * A tela das Automações, antes de elas existirem (29/09/2026).
 *
 * ── Divulga, e o botão não finge ──────────────────────────────────────────
 *
 * A tela conta o que o recurso vai fazer, sem prazo e sem falar de como ele
 * está sendo construído. Um "avise-me" que não guardasse nada seria a tela que
 * finge funcionar, e por isso o botão guarda de verdade: "Quero ser um dos
 * primeiros" grava a pessoa, a organização e a data (migração 066). Depois do
 * clique o botão dá lugar ao aviso de que ela está na lista — e o aviso vem do
 * servidor, então continua lá quando ela volta amanhã.
 *
 * ── A lista inteira, para a Cliqfy ────────────────────────────────────────
 *
 * A Operação Cliqfy, e quem administra uma organização interna da Cliqfy (por
 * ora, a Cliq Suporte, 06/10/2026), vê, abaixo dos exemplos, quem
 * pediu em todas as organizações, e baixa a planilha. Quem decide é a API, no
 * `ve_lista_de_espera` do `/v1/me`. É o mesmo lugar onde o pedido
 * nasce, e não existe outra tela interna onde pô-lo. Para qualquer outro papel
 * o bloco não é desenhado, e a rota recusaria de qualquer jeito.
 *
 * ── A cena é decorativa ───────────────────────────────────────────────────
 *
 * A regra da ilustração é inventada, e por isso ela é `aria-hidden`, como a
 * cena da tela da IA. O que ela mostra está dito em texto nos exemplos logo
 * abaixo.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { LugarNaListaDeEspera, PedidoNaListaDeEspera } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useListaDeEsperaDasAutomacoes,
  useLugarNaListaDasAutomacoes,
} from '@/shared/api/consultas';
import { useInvalidar, useQueryClient } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import { baixar, planilha } from '@/shared/lib/planilha';
import { type Tabela as TabelaDaPlanilha } from '@/shared/lib/planilha';
import { Botao, Icone, Tabela, type NomeDoIcone } from '@/shared/ui';
import './automacoes-em-breve.css';

/** Exportado para o teste de acessibilidade conferir a aba. */
export const TITULO_DA_ABA = 'Automações, em breve';

/**
 * O que as automações vão fazer, em palavras de qualquer segmento.
 *
 * "Oportunidade", "contato" e "etapa" são o vocabulário do próprio produto; o
 * que muda de uma organização para outra é o que ela vende, e nenhum exemplo
 * aqui depende disso.
 */
const EXEMPLOS: readonly { icone: NomeDoIcone; titulo: string; descricao: string }[] = [
  {
    icone: 'funil',
    titulo: 'Oportunidades que andam sozinhas',
    descricao:
      'Quando algo acontece, como uma resposta, uma proposta enviada ou um prazo vencido, a oportunidade vai para a etapa certa do funil.',
  },
  {
    icone: 'clientes',
    titulo: 'Contatos distribuídos na equipe',
    descricao:
      'Cada contato novo chega a quem deve atendê-lo, em rodízio ou pela regra que você definir, sem ninguém precisar repassar.',
  },
  {
    icone: 'relogio',
    titulo: 'Lembrete de retorno',
    descricao:
      'Quem não respondeu não cai no esquecimento: a tarefa de retomar a conversa aparece no prazo que você escolher.',
  },
  {
    icone: 'enviar',
    titulo: 'Mensagem de acompanhamento',
    descricao:
      'Depois de um atendimento, de uma proposta ou de um tempo sem conversa, a mensagem certa sai na hora certa.',
  },
];

/** `29/09/2026 14:03` — o dia e a hora de cada pedido. */
function quando(instante: string): string {
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AutomacoesEmBreve(): ReactNode {
  const { estado } = useSessao();
  const veALista = estado.nome === 'autenticado' && estado.me.ve_lista_de_espera === true;

  /* O título da aba volta ao que era ao sair — a mesma razão da tela da IA. */
  useEffect(() => {
    const anterior = document.title;
    document.title = TITULO_DA_ABA;
    return () => {
      document.title = anterior;
    };
  }, []);

  return (
    <div className="automacoes">
      <div className="automacoes__pagina">
        <section className="automacoes__heroi" aria-labelledby="automacoes-titulo">
          <div className="automacoes__texto">
            <h1 className="automacoes__titulo" id="automacoes-titulo">
              Automações que cuidam do trabalho repetitivo
            </h1>
            <p className="automacoes__descricao">
              Em breve, você vai dizer o que deve acontecer quando algo acontece, e o CliqCRM faz o
              resto: move oportunidades, distribui contatos, lembra retornos e acompanha quem ficou
              sem resposta.
            </p>
            <PedidoDeAcesso />
          </div>

          <Cena />
        </section>

        <ul className="automacoes__exemplos">
          {EXEMPLOS.map((exemplo) => (
            <li key={exemplo.titulo} className="automacoes__exemplo">
              <span className="automacoes__exemplo-icone" aria-hidden="true">
                <Icone nome={exemplo.icone} />
              </span>
              <h2 className="automacoes__exemplo-titulo">{exemplo.titulo}</h2>
              <p className="automacoes__exemplo-descricao">{exemplo.descricao}</p>
            </li>
          ))}
        </ul>

        {veALista && <QuemPediu />}
      </div>
    </div>
  );
}

/**
 * O botão, ou o aviso de que a pessoa já está na lista.
 *
 * ── Enquanto a pergunta não volta, nenhum dos dois ────────────────────────
 *
 * Mostrar o botão e trocá-lo pelo aviso meio segundo depois faria quem já pediu
 * ver, por um instante, o convite para pedir de novo.
 *
 * ── Se a pergunta falhar, o botão aparece ─────────────────────────────────
 *
 * Pedir de novo não custa nada: a rota não duplica e devolve a data do
 * primeiro pedido. Esconder o botão por uma falha de leitura tiraria a única
 * ação da tela de quem ainda não pediu.
 */
function PedidoDeAcesso(): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const invalidar = useInvalidar();
  const consulta = useLugarNaListaDasAutomacoes();
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  /*
   * O foco vai para o aviso só quando ELE nasceu de um clique.
   *
   * O botão sai da tela ao dar certo, e o foco que estava nele iria para o
   * começo da página. Quem abre a tela já estando na lista não clicou em nada,
   * e roubar o foco dela seria mover a leitura para um lugar que ela não pediu.
   */
  const [acabouDePedir, definirAcabouDePedir] = useState(false);
  const aviso = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (acabouDePedir) aviso.current?.focus();
  }, [acabouDePedir]);

  const pedir = (): void => {
    if (!api || enviando) return;
    definirEnviando(true);
    definirErro(null);
    void api
      .entrarNaListaDasAutomacoes()
      .then((lugar) => {
        cache.setQueryData<LugarNaListaDeEspera>(chaves.lugarNaListaDasAutomacoes(), lugar);
        invalidar([chaves.listaDeEsperaDasAutomacoes()]);
        definirAcabouDePedir(true);
      })
      .catch((causa: unknown) => {
        definirErro(
          recadoDeErro(causa, {
            generica: 'Não foi possível registrar seu pedido. Tente de novo.',
          }),
        );
      })
      .finally(() => definirEnviando(false));
  };

  if (consulta.isPending) return <div className="automacoes__pedido" />;

  if (consulta.data?.na_lista) {
    return (
      <div className="automacoes__pedido">
        <p className="automacoes__na-lista" ref={aviso} tabIndex={-1}>
          {/* O círculo com o visto, o mesmo de Tarefas: um segundo desenho
              para "feito" ensinaria que são coisas diferentes. */}
          <span className="automacoes__na-lista-icone" aria-hidden="true">
            <Icone nome="tarefas" />
          </span>
          <span>
            <b>Você está na lista.</b> Vai estar entre os primeiros a usar as automações.
          </span>
        </p>
        {consulta.data.entrou_em && (
          <p className="automacoes__nota">Pedido feito em {quando(consulta.data.entrou_em)}.</p>
        )}
      </div>
    );
  }

  return (
    <div className="automacoes__pedido">
      <Botao
        variante="primario"
        onClick={pedir}
        desabilitado={enviando || !api}
        motivo={enviando ? 'Registrando seu pedido…' : 'Sem sessão.'}
      >
        {enviando ? 'Registrando…' : 'Quero ser um dos primeiros'}
      </Botao>
      {/* Antes do clique, e não depois: quem pede precisa saber para onde vai
          o que o pedido leva. */}
      <p className="automacoes__nota">
        Ao pedir, a equipe da Cliqfy recebe seu nome e e-mail para falar com você sobre as
        automações.
      </p>
      {erro !== null && (
        <p className="automacoes__erro" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}

const COLUNAS_DOS_PEDIDOS = [
  { chave: 'organizacao', rotulo: 'Organização' },
  { chave: 'pessoa', rotulo: 'Pessoa' },
  { chave: 'email', rotulo: 'E-mail' },
  { chave: 'quando', rotulo: 'Pediu em' },
];

/** A planilha dos pedidos, na ordem da tela: do mais antigo para o mais novo. */
export function planilhaDosPedidos(itens: readonly PedidoNaListaDeEspera[]): TabelaDaPlanilha {
  return planilha(
    COLUNAS_DOS_PEDIDOS.map((coluna) => coluna.rotulo),
    itens.map((item) => [
      item.organizacao.nome,
      item.pessoa.nome,
      item.pessoa.email,
      quando(item.entrou_em),
    ]),
  );
}

/** Quem pediu, em todas as organizações. Só para a equipe da Cliqfy. */
function QuemPediu(): ReactNode {
  const consulta = useListaDeEsperaDasAutomacoes(true);
  const itens = consulta.data?.itens ?? [];
  const organizacoes = new Set(itens.map((item) => item.organizacao.id)).size;

  return (
    <section className="automacoes__pedidos" aria-labelledby="automacoes-pedidos-titulo">
      <div className="automacoes__pedidos-topo">
        <div>
          <h2 className="automacoes__pedidos-titulo" id="automacoes-pedidos-titulo">
            Quem pediu
          </h2>
          <p className="automacoes__pedidos-apoio">
            Só a equipe da Cliqfy vê este bloco.
            {itens.length > 0 &&
              ` ${itens.length} ${itens.length === 1 ? 'pedido' : 'pedidos'} de ${organizacoes} ${
                organizacoes === 1 ? 'organização' : 'organizações'
              }.`}
          </p>
        </div>
        <Botao
          onClick={() =>
            baixar(planilhaDosPedidos(itens), 'lista-de-espera-automacoes.xlsx', 'Lista de espera')
          }
          desabilitado={itens.length === 0}
          motivo="Ninguém pediu ainda."
        >
          Baixar planilha
        </Botao>
      </div>

      {consulta.isPending ? (
        <p className="automacoes__pedidos-apoio">Buscando a lista…</p>
      ) : consulta.isError ? (
        <p className="automacoes__erro" role="alert">
          {recadoDeErro(consulta.error, { generica: 'Não foi possível ler a lista agora.' })}
        </p>
      ) : itens.length === 0 ? (
        <p className="automacoes__pedidos-apoio">Ninguém pediu ainda.</p>
      ) : (
        <Tabela
          rotulo="Pedidos na lista de espera das automações"
          densa
          colunas={COLUNAS_DOS_PEDIDOS}
          chaveDe={(_linha, indice) =>
            `${itens[indice]!.organizacao.id}:${itens[indice]!.pessoa.id}`
          }
          linhas={itens.map((item) => ({
            organizacao: item.organizacao.nome,
            pessoa: item.pessoa.nome,
            email: item.pessoa.email,
            quando: quando(item.entrou_em),
          }))}
        />
      )}
    </section>
  );
}

/**
 * A ilustração: uma regra montada, do gatilho às duas ações.
 *
 * Os três passos acendem em sequência, como se a regra estivesse rodando —
 * ver `automacoes-em-breve.css`. Com `prefers-reduced-motion`, ficam acesos.
 */
function Cena(): ReactNode {
  return (
    <div className="automacoes__cena" aria-hidden="true">
      <div className="automacoes__regra">
        <div className="automacoes__regra-topo">
          <span className="automacoes__regra-nome">Retomar quem sumiu</span>
          <span className="automacoes__chave">
            <span className="automacoes__chave-bolinha" />
            Ligada
          </span>
        </div>

        <div className="automacoes__passo automacoes__passo--quando">
          <span className="automacoes__passo-icone">
            <Icone nome="relogio" />
          </span>
          <span className="automacoes__passo-texto">
            <b>Quando</b>
            <span>o contato fica 2 dias sem responder</span>
          </span>
        </div>

        <span className="automacoes__ligacao" />

        <div className="automacoes__passo automacoes__passo--entao">
          <span className="automacoes__passo-icone">
            <Icone nome="tarefa-nova" />
          </span>
          <span className="automacoes__passo-texto">
            <b>Então</b>
            <span>cria a tarefa “Retomar contato”</span>
          </span>
        </div>

        <span className="automacoes__ligacao" />

        <div className="automacoes__passo automacoes__passo--tambem">
          <span className="automacoes__passo-icone">
            <Icone nome="enviar" />
          </span>
          <span className="automacoes__passo-texto">
            <b>E também</b>
            <span>envia a mensagem de acompanhamento</span>
          </span>
        </div>
      </div>
    </div>
  );
}
